import React, { useRef } from 'react'
import { useSelector } from 'react-redux'
import { uniqueFilename } from '@/utils/unique'
import { isSupportedImageFile, IMAGE_UPLOAD_ACCEPT } from '@/utils/supportedImageFormats'
import { setUploads, uploadFile } from '@/store/slices/uploads/actions'
import { setCallbackFromCreationPage } from '@/store/slices/editor/action'
import { setOpenModalTryPremium } from '@/store/slices/user/actions'
import { selectUserIsSuper, selectUser } from '@/store/slices/user/selectors'
import { useAppDispatch } from '@/store/store'
import { ActionType } from '@/store/slices/editor/actionTypes'
import { customAmplitude } from '@/utils/customAmplitude'
import useModal from '@/hooks/useModal'
import { ShowModalType } from '@/scenes/engine/common/constants'
import SuperIcon from '@/scenes/Editor/components/Icons/SuperIcon'
import SectionRow from '../components/SectionRow'
import PreviewCard from '../components/PreviewCard'
import type { HomeFeedAiItem, HomeFeedAiSection } from '@/services/api'

const PREVIEW_HEIGHT = 208
// iOS `MagicTemplateSection.renderingAspectRatio`: 0.0 aspect ratio falls back to 2:3.
const DEFAULT_AI_VIDEO_ASPECT_RATIO = 2 / 3

// Copied verbatim from iOS `MagicTemplate.aiModelsPremiumSource`: AI-video items
// are `isSuper`, so the paywall source is `BtSubscriptionCustomModels`.
const AI_VIDEO_PREMIUM_SOURCE = 'BtSubscriptionCustomModels'

/** Payload handed to AI Studio to generate a video from the item's motion prompt. */
export interface AiVideoHandoffValue {
  id: number
  title: string
  videoPrompt: string | null
  prompt: string
}

interface Props {
  section: HomeFeedAiSection
}

/**
 * AI-video feed section. Shown to all users; only Super users can use it.
 * Non-super clicks open the Super paywall (source/analytics copied from iOS).
 * Super clicks browse an image, then hand off to AI Studio which generates a
 * video from the item's `videoPrompt` (mirrors the iOS AI-video flow).
 */
function AiVideoFeedRow({ section }: Props) {
  const dispatch = useAppDispatch()
  const userIsSuper = useSelector(selectUserIsSuper)
  const user = useSelector(selectUser)
  const { setShowModalType } = useModal()
  const inputFileRef = useRef<HTMLInputElement>(null)
  const pendingItemRef = useRef<HomeFeedAiItem | null>(null)

  const pickImageForItem = (item: HomeFeedAiItem) => {
    pendingItemRef.current = item
    inputFileRef.current?.click()
  }

  const runItemFlow = (item: HomeFeedAiItem) => {
    if (!userIsSuper) {
      dispatch(
        setOpenModalTryPremium({
          isOpen: true,
          source: AI_VIDEO_PREMIUM_SOURCE,
          // Resume straight into the picker rather than re-entering runItemFlow:
          // this callback is stored and invoked after purchase, so it would still
          // see the `userIsSuper === false` captured here and re-open the paywall.
          callback: () => pickImageForItem(item),
        }),
      )
      customAmplitude('Premium Prompt', { Source: AI_VIDEO_PREMIUM_SOURCE, Type: 'Super' })
      return
    }
    if (!user) {
      setShowModalType(ShowModalType.SIGN_IN)
      return
    }
    pickImageForItem(item)
  }

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    const item = pendingItemRef.current
    // Read the file BEFORE resetting the input: `value = ''` clears the live
    // `e.target.files`, so capturing it first avoids an empty selection.
    if (!files || !files.length || !item) {
      e.target.value = ''
      return
    }
    const file = files[0]
    e.target.value = ''
    // `accept` only filters the file dialog's default view — a user can still pick
    // an unsupported file, so validate against the same list before uploading.
    if (!isSupportedImageFile(file)) {
      setShowModalType(ShowModalType.UNSUPPORTED_FILE_TYPE)
      return
    }
    const updatedFile = new File([file], uniqueFilename(file.name))
    // Deliberately not `.unwrap()`ed: the `uploadFile` thunk always rejects (it
    // touches its IndexedDB handle before `onsuccess` assigns it), while the real
    // upload completes later in the reader's `onload`. Treating that rejection as
    // failure would skip the handoff below and never open AI Studio. Completion is
    // observed via the `uploads` list instead (see useCreationPageUpload).
    // Clear any uploads left from an earlier editor session first: otherwise the
    // non-empty list redirects to AI Studio before this file lands, and AI Studio
    // boots (and generates) from that stale `uploads[0]`.
    dispatch(setUploads([]))
    await dispatch(uploadFile({ file: updatedFile, fromCreationPage: true }))
    const value: AiVideoHandoffValue = {
      id: item.id,
      title: item.title,
      videoPrompt: item.videoPrompt,
      prompt: item.prompt,
    }
    dispatch(setCallbackFromCreationPage({ action: ActionType.AI_VIDEO, value }))
    // AI-video items are Magic Templates on iOS; reuse the existing dashboard-safe
    // `Magic Template` Source. The category id (113/132) distinguishes AI-video.
    customAmplitude('Create', {
      Source: 'Magic Template',
      'Template id': item.id,
      'Template Category id': section.id,
    })
  }

  if (!section.items.length) return null

  // AI-video previews are short mp4 clips (asset `file` is a video, not an image),
  // so they render in a looping <video> rather than the before/after image swipe.
  // The row fixes the card height; each card derives its width from this ratio.
  const aspectRatio = section.aspectRatio > 0 ? section.aspectRatio : DEFAULT_AI_VIDEO_ASPECT_RATIO

  return (
    <>
      <SectionRow title={section.title} itemHeight={PREVIEW_HEIGHT}>
        {section.items.map(item => {
          const videoSrc = item.assets?.[0]?.file
          if (!videoSrc) return null
          return (
            <PreviewCard
              key={item.id}
              videoSrc={videoSrc}
              aspectRatio={aspectRatio}
              // The Super badge marks the row as gated, so it's only for users who
              // aren't Super yet — Super users already have access.
              badge={userIsSuper ? undefined : <SuperIcon width={16} height={16} />}
              onClick={() => runItemFlow(item)}
            />
          )
        })}
      </SectionRow>
      <input
        onChange={handleFileInput}
        type="file"
        ref={inputFileRef}
        style={{ display: 'none' }}
        accept={IMAGE_UPLOAD_ACCEPT}
      />
    </>
  )
}

export default AiVideoFeedRow
